import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DAYS = [
  ["mon", "Monday"],
  ["tue", "Tuesday"],
  ["wed", "Wednesday"],
  ["thu", "Thursday"],
  ["fri", "Friday"],
  ["sat", "Saturday"],
  ["sun", "Sunday"],
] as const;
type Day = (typeof DAYS)[number][0];
type DayDraft = {
  status: "missing" | "closed" | "open";
  open: string;
  close: string;
};
export interface ReceivingHoursDraft {
  configured: boolean;
  days: Record<Day, DayDraft>;
  unrecognized: boolean;
}

// This is a form adapter, not a replacement for the Server hours parser.
// In particular, absent days must never be interpreted as explicitly closed.
export function toReceivingHoursDraft(raw: unknown): ReceivingHoursDraft {
  const days = {} as Record<Day, DayDraft>;
  const record = raw && typeof raw === "object" ? raw : {};
  let unrecognized = false;
  for (const [day] of DAYS) {
    const value = day in record ? Reflect.get(record, day) : undefined;
    if (value === null) days[day] = { status: "closed", open: "", close: "" };
    else if (
      value &&
      typeof value === "object" &&
      typeof value.open === "string" &&
      typeof value.close === "string"
    ) {
      days[day] = { status: "open", open: value.open, close: value.close };
      if (Object.keys(value).some((key) => key !== "open" && key !== "close"))
        unrecognized = true;
    } else {
      days[day] = { status: "missing", open: "", close: "" };
      if (value !== undefined) unrecognized = true;
    }
  }
  if (
    raw != null &&
    (typeof raw !== "object" ||
      Array.isArray(raw) ||
      Object.keys(raw).some((key) => !DAYS.some(([day]) => day === key)))
  )
    unrecognized = true;
  return { configured: raw != null, days, unrecognized };
}

export function receivingHoursInput(draft: ReceivingHoursDraft): {
  value?: Record<Day, { open: string; close: string } | null> | null;
  error?: string;
} {
  if (!draft.configured) return { value: null };
  const value = {} as Record<Day, { open: string; close: string } | null>;
  for (const [day, label] of DAYS) {
    const entry = draft.days[day];
    if (entry.status === "missing")
      return {
        error: `${label}: choose Open or Closed. Every day must be specified.`,
      };
    if (entry.status === "closed") {
      value[day] = null;
      continue;
    }
    const minutes = (text: string) =>
      /^\d{2}:[0-5]\d$/.test(text)
        ? Number(text.slice(0, 2)) * 60 + Number(text.slice(3))
        : NaN;
    const open = minutes(entry.open);
    const close = minutes(entry.close);
    if (
      !Number.isFinite(open) ||
      open >= 1440 ||
      !Number.isFinite(close) ||
      close <= open
    ) {
      return {
        error: `${label}: use HH:MM, open between 00:00 and 23:59, and close after open. For overnight hours use 24:00 or later (e.g. 27:00).`,
      };
    }
    value[day] = { open: entry.open, close: entry.close };
  }
  return { value };
}

export function ReceivingHoursEditor({
  value,
  onChange,
}: {
  value: ReceivingHoursDraft;
  onChange: (value: ReceivingHoursDraft) => void;
}) {
  return (
    <div className="space-y-3 border-t pt-3">
      <Label htmlFor="dl-hours-configured">Receiving hours</Label>
      <select
        id="dl-hours-configured"
        className="w-full rounded-md border bg-background p-2 text-sm"
        value={value.configured ? "configured" : "missing"}
        onChange={(event) =>
          onChange({
            ...value,
            configured: event.target.value === "configured",
          })
        }
      >
        <option value="missing">Not configured</option>
        <option value="configured">Specify weekly hours</option>
      </select>
      <p className="text-xs text-muted-foreground">
        Facility receiving hours in the verified timezone, separate from local
        provider pickup windows. Not configured means unknown; explicitly
        closing every day means no receiving availability.
      </p>
      {value.unrecognized && (
        <p className="text-sm text-amber-700">
          Existing hours contain unsupported data. Unchanged hours are
          preserved; editing this schedule replaces it with the days below.
        </p>
      )}
      {value.configured && (
        <>
          <p className="text-xs text-muted-foreground">
            One window per day. Use HH:MM; overnight closes use hours above 24
            (27:00 means 03:00 the next day).
          </p>
          {DAYS.map(([day, label]) => {
            const entry = value.days[day];
            const change = (patch: Partial<DayDraft>) =>
              onChange({
                ...value,
                days: { ...value.days, [day]: { ...entry, ...patch } },
              });
            return (
              <div
                key={day}
                className="grid grid-cols-[6rem_1fr] gap-2 items-center"
              >
                <Label htmlFor={`dl-${day}-status`}>{label}</Label>
                <select
                  id={`dl-${day}-status`}
                  className="rounded-md border bg-background p-2 text-sm"
                  value={entry.status}
                  onChange={(event) => {
                    const status = event.target.value;
                    if (
                      status === "missing" ||
                      status === "closed" ||
                      status === "open"
                    )
                      change({ status });
                  }}
                >
                  <option value="missing">Not specified</option>
                  <option value="closed">Closed</option>
                  <option value="open">Open</option>
                </select>
                {entry.status === "open" && (
                  <div className="col-span-2 grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor={`dl-${day}-open`}>{label} opens</Label>
                      <Input
                        id={`dl-${day}-open`}
                        value={entry.open}
                        placeholder="HH:MM"
                        onChange={(event) =>
                          change({ open: event.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor={`dl-${day}-close`}>{label} closes</Label>
                      <Input
                        id={`dl-${day}-close`}
                        value={entry.close}
                        placeholder="HH:MM"
                        onChange={(event) =>
                          change({ close: event.target.value })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
